import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useMatch } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { isDefined } from '../../utils/variables';
import { usePendingProjectTransfersQuery } from '../components/companies/pending-project-transfers/use-pending-project-transfers-query';
import { FeatureFlagsIcon } from '../components/feature-flags/feature-flags-icon';
import { Icon } from '../components/icon/icon';
import { LanguageSwitcher } from '../components/language-switcher/language-switcher';
import { NotificationBellIcon } from '../components/notification-center/notification-bell-icon';
import { PendingTransferIndicator } from '../components/pending-transfer-indicator/pending-transfer-indicator';
import { Stack } from '../components/stack/stack';
import { UserMenuDropdown } from '../components/user-menu-dropdown';
import { useAppContext, UserRole } from '../state/app-state';
import { ProjectAccessProvider, useProjectAccess } from '../state/project-access-state';
import { useTheme } from '../state/theme-state';
import { isAccessTokenRedirect } from '../utils/access-token-redirect-flag';

type NavItemKey =
  | 'projects'
  | 'users'
  | 'companies'
  | 'accounts'
  | 'jobs'
  | 'accessTokens'
  | 'devTools';
type NavItemDefinition = { key: NavItemKey; to: string };

/** Sub-menu item for company or project section. Rendered only when `available` is not false. */
interface SubMenuItem {
  label: string;
  to: string;
  end?: boolean;
  /** Show pending transfer indicator next to the label. */
  showPendingIndicator?: boolean;
  /** When false, the item is hidden from the sub-menu. Defaults to true. */
  available?: boolean;
}

const navItemsByRole: Record<UserRole, NavItemDefinition[]> = {
  [UserRole.OrganizationAdmin]: [
    { key: 'projects', to: '/projects' },
    { key: 'users', to: '/users' },
    { key: 'companies', to: '/companies' },
    { key: 'accounts', to: '/accounts' },
    { key: 'jobs', to: '/jobs' },
    { key: 'accessTokens', to: '/access-tokens' },
    { key: 'devTools', to: '/dev-tools/client-version' },
  ],
  [UserRole.CompanyAdmin]: [
    { key: 'projects', to: '/projects' },
    { key: 'companies', to: '/companies' },
  ],
  [UserRole.ProjectUser]: [{ key: 'projects', to: '/projects' }],
};

export const AppLayout = () => (
  <ProjectAccessProvider>
    <AppLayoutInner />
  </ProjectAccessProvider>
);

const AppLayoutInner = () => {
  const { currentUser, isOrganizationAdmin } = useAppContext();
  const { projectAccess } = useProjectAccess();
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { hasAccessToRoute } = theme;

  const helpCenterUrl = theme.getHelpCenterUrl();
  const shellBackgroundColor = theme.getShellBackgroundColor();
  const topMenuBackgroundColor = theme.getTopMenuBackgroundColor();
  const topMenuLogotype = theme.getTopMenuLogotype();
  const topMenuTextColor = theme.getTopMenuTextColor();
  const topMenuTextHoverColor = theme.getTopMenuTextHoverColor();
  const notificationsLabel = t('notifications.ariaLabel', { ns: 'navigation' });
  const helpCenterTitle = t('header.helpCenter.title', { ns: 'navigation' });
  const logoAlt = t('header.logo.alt', { ns: 'navigation' });

  const navigationLabels = React.useMemo<Record<NavItemKey, string>>(
    () => ({
      projects: t('menu.projects.title', { ns: 'navigation' }),
      users: t('menu.users.title', { ns: 'navigation' }),
      companies: t('menu.companies.title', { ns: 'navigation' }),
      accounts: t('menu.accounts.title', { ns: 'navigation' }),
      jobs: t('menu.jobs.title', { ns: 'navigation' }),
      accessTokens: t('menu.accessTokens.title', { ns: 'navigation' }),
      devTools: t('menu.devTools.title', { ns: 'navigation' }),
    }),
    [t],
  );

  const { data: pendingTransfersData } = usePendingProjectTransfersQuery();
  const { companyIdsWithPending, projectIdsWithPending } = useMemo(() => {
    const list = pendingTransfersData?.data ?? [];
    const pending = list.filter((i) => i.status === 'PENDING');
    return {
      companyIdsWithPending: new Set(pending.map((i) => i.companyId)),
      projectIdsWithPending: new Set(pending.map((i) => i.projectId)),
    };
  }, [pendingTransfersData]);
  const hasAnyPendingTransfer = companyIdsWithPending.size > 0;

  const selectedRole = currentUser?.selectedRole ?? UserRole.OrganizationAdmin;
  const topMenuItems = navItemsByRole[selectedRole] ?? navItemsByRole[UserRole.ProjectUser];
  const companiesMatchWildcard = useMatch('/companies/:companyId/*');
  const companiesMatchExact = useMatch('/companies/:companyId');
  const companiesMatch = companiesMatchWildcard ?? companiesMatchExact;
  const matchedCompanyId = companiesMatch?.params?.companyId;
  // Exclude reserved path segment so /companies/pending-transfers is not treated as a company
  const selectedCompanyId =
    matchedCompanyId && matchedCompanyId !== 'pending-transfers' ? matchedCompanyId : undefined;
  const projectsMatchWildcard = useMatch('/projects/:projectId/*');
  const projectsMatchExact = useMatch('/projects/:projectId');
  const projectsMatch = projectsMatchWildcard ?? projectsMatchExact;
  const selectedProjectId = projectsMatch?.params?.projectId;
  const isGcpFullBleed = useMatch('/projects/:projectId/geodata/:geodataId/viewer');
  const isGeodataUploadFullBleed = useMatch('/projects/:projectId/geodata/upload');
  const isSkyViewFullBleed = useMatch('/projects/:projectId/viewer');
  const isFileManagerFullBleed = useMatch('/projects/:projectId/filemanager');

  const isFullBleed = useMemo(() => {
    return isDefined(
      isGcpFullBleed ?? isGeodataUploadFullBleed ?? isSkyViewFullBleed ?? isFileManagerFullBleed,
    );
  }, [isGcpFullBleed, isGeodataUploadFullBleed, isSkyViewFullBleed, isFileManagerFullBleed]);

  // Check if we're coming from an access token redirect - hide layout in this case.
  // The module-scoped flag survives internal navigations that lose location.state,
  // and is isolated per JS context (iframe vs parent window).
  const hideLayout = Boolean(
    (location.state as { fromAccessTokenRedirect?: boolean } | null)?.fromAccessTokenRedirect ||
      isAccessTokenRedirect(),
  );
  const showLicensesTab = FeatureFlagsManager.instance.isEnabled('SK-5667-licenses-page');
  const companiesSectionMatch = useMatch('/companies/*');
  const pendingTransferTooltip = t('menu.pendingProjectTransfers.attentionTooltip', {
    ns: 'navigation',
  });
  const companySubMenuItems = React.useMemo<SubMenuItem[]>(() => {
    if (!companiesSectionMatch) {
      return [];
    }

    const items: SubMenuItem[] = [];

    if (selectedCompanyId) {
      items.push({
        label: t('menu.overview.title', { ns: 'navigation' }),
        to: `/companies/${selectedCompanyId}`,
        end: true,
      });

      if (showLicensesTab) {
        items.push({
          label: t('menu.licenses.title', { ns: 'navigation' }),
          to: `/companies/${selectedCompanyId}/licenses`,
          end: true,
        });
      }

      items.push({
        label: t('menu.companyAdmins.title', { ns: 'navigation' }),
        to: `/companies/${selectedCompanyId}/admins`,
        end: true,
      });

      items.push({
        label: t('menu.pendingProjectTransfers.title', { ns: 'navigation' }),
        to: `/companies/${selectedCompanyId}/pending-transfers`,
        end: true,
        showPendingIndicator: companyIdsWithPending.has(selectedCompanyId),
      });
    }

    return items;
  }, [companiesSectionMatch, selectedCompanyId, showLicensesTab, companyIdsWithPending, t]);

  const projectSubMenuItems = React.useMemo<SubMenuItem[]>(() => {
    if (!selectedProjectId) {
      return [];
    }

    // When projectAccess is not yet loaded, default to least privilege (hide restricted items)
    // to avoid briefly showing sub-menu items the user may not have access to.
    const isOrgAdmin = projectAccess?.isOrgAdmin ?? false;
    const isProjectUser = projectAccess ? !projectAccess.isAdmin : true;
    // Hide design-license-restricted modules until the company license type is confirmed.
    // While isDesignLicenseOnly is undefined (company loading), treat as restricted to
    // avoid a brief flash of modules that may be hidden once the company data arrives.
    // Org admins and moba theme always have full access regardless.
    const isDesignLicenseRestricted =
      !isOrgAdmin && projectAccess?.isDesignLicenseOnly !== false && !theme.isMobaTheme();

    return [
      {
        label: t('menu.overview.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}`,
        end: true,
        available: hasAccessToRoute(`/projects/${selectedProjectId}`),
      },
      {
        label: t('menu.skyview.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/viewer`,
        end: true,
        available: hasAccessToRoute(`/projects/${selectedProjectId}/viewer`),
      },
      {
        label: t('menu.timeline.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/timeline`,
        end: true,
        available:
          !isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${selectedProjectId}/timeline`),
      },
      {
        label: t('menu.cloudProcessing.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/geodata`,
        end: true,
        available:
          !isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${selectedProjectId}/geodata`),
      },
      {
        label: t('menu.fileManager.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/filemanager`,
        end: true,
        available: hasAccessToRoute(`/projects/${selectedProjectId}/filemanager`),
      },
      {
        label: t('menu.projectAttendee.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/users`,
        end: true,
        available: hasAccessToRoute(`/projects/${selectedProjectId}/users`),
      },
      {
        label: t('menu.email.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/email`,
        end: true,
        available: hasAccessToRoute(`/projects/${selectedProjectId}/email`),
      },
      {
        label: t('menu.transfer.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/transfer`,
        end: true,
        showPendingIndicator: projectIdsWithPending.has(selectedProjectId),
        available: isOrgAdmin,
      },
      {
        label: t('menu.integrations.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/integrations`,
        end: true,
        available:
          !isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${selectedProjectId}/integrations`),
      },
      {
        label: t('menu.settings.title', { ns: 'navigation' }),
        to: `/projects/${selectedProjectId}/settings`,
        end: true,
        available: !isProjectUser && hasAccessToRoute(`/projects/${selectedProjectId}/settings`),
      },
    ];
  }, [selectedProjectId, projectAccess, theme, t, hasAccessToRoute, projectIdsWithPending]);

  const activeSubMenuItems =
    projectSubMenuItems.length > 0 ? projectSubMenuItems : companySubMenuItems;
  const visibleSubMenuItems = useMemo(
    () => activeSubMenuItems.filter((item) => item.available !== false),
    [activeSubMenuItems],
  );

  // Show skeleton placeholders until access is resolved, and while the company license type
  // is loading for company admin users.
  const isSubMenuLoading =
    projectSubMenuItems.length > 0 &&
    (projectAccess === undefined ||
      (projectAccess.isAdmin === true &&
        !projectAccess.isOrgAdmin &&
        projectAccess.isDesignLicenseOnly === undefined &&
        !theme.isMobaTheme()));

  // If coming from access token redirect, render without layout (fullscreen)
  if (hideLayout) {
    return (
      <FullscreenContainer>
        <Outlet />
      </FullscreenContainer>
    );
  }

  return (
    <Shell backgroundColor={shellBackgroundColor} id="mainLayout">
      <Header backgroundColor={topMenuBackgroundColor} textColor={topMenuTextColor}>
        <LogoCell justifyContent="center">
          <LogoLink to="/projects">
            <img alt={logoAlt} src={topMenuLogotype} />
          </LogoLink>
        </LogoCell>
        <TopMenu textColor={topMenuTextColor} textHoverColor={topMenuTextHoverColor}>
          <ul>
            {topMenuItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                  to={item.to}
                >
                  <TopMenuLinkContent>
                    {navigationLabels[item.key] ?? item.key}
                    {item.key === 'companies' && hasAnyPendingTransfer && (
                      <PendingTransferIndicator
                        style={{ position: 'relative', bottom: '0.35rem' }}
                        title={pendingTransferTooltip}
                      />
                    )}
                  </TopMenuLinkContent>
                </NavLink>
              </li>
            ))}
          </ul>
        </TopMenu>
        {isOrganizationAdmin && <FeatureFlagsIcon />}
        <NotificationBell aria-label={notificationsLabel} textColor={topMenuTextColor}>
          <NotificationBellIcon />
        </NotificationBell>
        <HelpCenterLink
          aria-label={helpCenterTitle}
          href={helpCenterUrl}
          rel="noopener noreferrer"
          target="_blank"
          textColor={topMenuTextColor}
          textHoverColor={topMenuTextHoverColor}
          title={helpCenterTitle}
        >
          <Icon icon="question-circle" />
        </HelpCenterLink>
        <LanguageSwitcher textColor={topMenuTextColor} />
        <UserMenuDropdown textColor={topMenuTextColor} />
        {visibleSubMenuItems.length > 0 && (
          <SubMenu>
            <ul>
              {isSubMenuLoading
                ? skeletonWidths.map((width, i) => (
                    <li key={i}>
                      <SkeletonBar style={{ width }} />
                    </li>
                  ))
                : visibleSubMenuItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        className={({ isActive }) => (isActive ? 'active' : undefined)}
                        end={item.end}
                        to={item.to}
                      >
                        <SubMenuLinkContent>
                          {item.label}
                          {item.showPendingIndicator && (
                            <PendingTransferIndicator
                              style={{ marginLeft: '0.35rem' }}
                              title={pendingTransferTooltip}
                            />
                          )}
                        </SubMenuLinkContent>
                      </NavLink>
                    </li>
                  ))}
            </ul>
          </SubMenu>
        )}
      </Header>
      <Main fullBleed={isFullBleed} id="mainContent">
        <Outlet />
      </Main>
    </Shell>
  );
};

const FullscreenContainer = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const Shell = styled.div<{ backgroundColor: string }>`
  height: 100vh;
  width: 100vw;
  display: grid;
  grid-template-rows: auto 1fr;
  background-color: ${(props) => props.backgroundColor};
`;

const Header = styled.header<{ backgroundColor: string; textColor: string }>`
  display: grid;
  grid-template-columns: auto 1fr auto auto auto auto auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'logotype top-menu feature-flags notification-bell help-center language-switcher user-profile'
    'sub-menu sub-menu sub-menu sub-menu sub-menu sub-menu sub-menu';
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  z-index: 10;
`;
const TopMenuLinkContent = styled.span`
  display: inline-flex;
  align-items: center;
`;

const TopMenu = styled.nav<{ textColor: string; textHoverColor: string }>`
  grid-area: top-menu;
  grid-column: 2 / 3;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;

  ul {
    display: flex;
    align-items: stretch;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: grid;
      place-items: stretch;
      font-size: 0.95rem;
      font-weight: 500;
      color: ${(props) => props.textColor};

      a {
        display: grid;
        place-items: center;
        padding: 5px 20px;
        color: inherit;
        text-decoration: none;
      }

      &:hover,
      a.active {
        background-color: ${({ theme }) => theme.color.gray.darkest};
        color: ${(props) => props.textHoverColor ?? props.textColor};
      }

      a.active {
        background-color: ${({ theme }) => theme.color.gray.darkest};
        color: ${(props) => props.textHoverColor ?? props.textColor};
      }
    }
  }
`;

const SubMenuLinkContent = styled.span`
  display: inline-flex;
  align-items: center;
`;

const LogoCell = styled(Stack)`
  grid-area: logotype;
  padding: 0 1.5rem;

  img,
  span {
    height: 48px;
    display: block;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.05em;

  & > img {
    height: 2em;
  }
`;

const HelpCenterLink = styled.a<{ textColor: string; textHoverColor: string }>`
  grid-area: help-center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  font-size: 1.5rem;
  text-decoration: none;

  &:hover {
    background-color: ${(props) => props.theme.color.gray.darkest};
    color: ${(props) => props.textHoverColor ?? props.textColor ?? 'inherit'} !important;
  }
`;

const Main = styled.main<{ fullBleed?: boolean }>`
  position: relative;
  padding: ${(props) => (props.fullBleed ? '0' : '2rem')};
  height: 100%;
  overflow-y: auto;
`;

const NotificationBell = styled.div<{ textColor: string }>`
  grid-area: notification-bell;
  display: flex;
  align-items: center;
  color: ${(props) => props.textColor};
  justify-content: center;
  color: #ffffff;
  font-size: 1.5rem;
  text-decoration: none;
`;

const SubMenu = styled.nav`
  grid-area: sub-menu;
  width: 100%;
  background-color: ${({ theme }) => theme.color.gray.lightest};
  padding: 0 1.5rem;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    gap: 1.5rem;
    height: 3em;
    align-items: center;
  }

  li {
    display: flex;
    align-items: center;
  }

  a {
    color: ${({ theme }) => theme.color.gray.darkest};
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    padding-bottom: 6px;
    border-bottom: 2px solid transparent;
    transition: border-color 120ms ease;
  }

  a:hover,
  a.active {
    border-bottom-color: ${({ theme }) => theme.color.brand.dark};
  }
`;

const skeletonWidths: string[] = ['5.5rem', '7rem', '4.5rem', '6rem', '5rem'];

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`;

const SkeletonBar = styled.span<{ dark?: boolean }>`
  display: inline-block;
  width: 5rem;
  height: 0.9rem;
  border-radius: 4px;
  background-color: ${({ dark, theme }) => (dark ? theme.color.gray.dark : theme.color.gray.light)};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;
